import type { Gender } from "@iec-id/shared/contracts/profile";
import {
	formatBirthDate,
	maskBirthDateInput,
	parseBirthDate,
} from "@iec-id/shared/utils/birth-date";
import { Input, initialsOf, RadioSegment } from "@iec-id/ui";
import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { AvatarPicker } from "../avatar-picker";
import { firstErrorKey } from "../form-errors";
import { useOnboardingT } from "../onboarding-i18n";
import { StepTitle } from "../step-title";
import { StepForm, type StepProps } from "./step-shell";

/** Ошибка разбора даты → ключ подписи под полем. */
const BIRTH_DATE_ERROR_KEYS = {
	required: "about.errors.birthDateRequired",
	format: "about.errors.birthDateFormat",
	unreal: "about.errors.birthDateUnreal",
	future: "about.errors.birthDateFuture",
} as const;

/**
 * Шаг 2 — дата рождения и пол (макет 4b-2, поле страны снято).
 *
 * Страну здесь больше не спрашивают (решение владельца 2026-08-21). Ответ был
 * нужен ровно одному потребителю — выбору платёжного контура, — а спрашивался
 * у всех и на втором шаге регистрации, когда до оплаты может не дойти вовсе.
 * Контур теперь выбирается по языку профиля
 * (`billing/_internal/select-provider.ts`), и лишний вопрос исчез.
 *
 * Гейт 16+ живёт на сервере, а не здесь: возраст — вопрос юридический
 * (инвариант №4), и решение о нём принимается в одном месте. Клиент проверяет
 * только то, что дата вообще существует; «моложе шестнадцати» возвращается
 * ответом `rejected` и разворачивает мастер в отдельный экран.
 *
 * Пол обязателен и по умолчанию — мужской (решение владельца 2026-08-22).
 * Третий вариант — «не указывать», не пустота: пустое значение неотличимо от
 * «не дошёл до поля», а заглушка аватара всё равно обязана что-то показать.
 *
 * Фотография спрашивается здесь, а не на первом шаге: пока пол неизвестен,
 * заглушка воксельного аватара половине людей досталась бы чужого пола.
 * Переключение пола перерисовывает её сразу — картинка идёт от того же
 * `userId`, меняется только набор причёсок и одежды.
 */
export function AboutStep({ state, submit, pending, error }: StepProps) {
	const t = useOnboardingT();
	const { draft } = state;
	const [avatarKey, setAvatarKey] = useState<string | null>(null);

	const form = useForm({
		defaultValues: {
			birthDate: formatBirthDate(draft.birthDate),
			gender: draft.gender ?? ("male" satisfies Gender),
		},
		onSubmit: ({ value }) => {
			const parsed = parseBirthDate(value.birthDate);
			if (!parsed.ok) return;
			submit({
				step: "about",
				birthDate: parsed.iso,
				gender: value.gender,
				...(avatarKey ? { avatarKey } : {}),
			});
		},
	});

	return (
		<>
			<StepTitle tooltip={t("about.titleTooltip")}>{t("about.title")}</StepTitle>

			<form.Subscribe selector={(formState) => formState.values.gender}>
				{(gender) => (
					<AvatarPicker
						initials={initialsOf(draft.firstName ?? draft.nickname)}
						initialUrl={placeholderUrlFor(draft.avatarUrl, gender)}
						hasCustomAvatar={draft.hasCustomAvatar}
						onUploaded={setAvatarKey}
					/>
				)}
			</form.Subscribe>

			<StepForm onSubmit={() => void form.handleSubmit()} pending={pending} error={error}>
				<form.Field
					name="birthDate"
					validators={{
						onSubmit: ({ value }) => {
							const parsed = parseBirthDate(value);
							return parsed.ok ? undefined : BIRTH_DATE_ERROR_KEYS[parsed.error];
						},
					}}
				>
					{(field) => {
						const key = firstErrorKey(field.state.meta.errors);
						return (
							<Input
								label={t("about.birthDate")}
								name="birthDate"
								inputMode="numeric"
								autoComplete="bday"
								enterKeyHint="next"
								formatGuide={t("about.birthDateHint")}
								autoFocus
								value={field.state.value}
								disabled={pending}
								// Точки расставляются на лету: человек вводит восемь цифр,
								// а формат «ДД.ММ.ГГГГ» держим мы, а не он.
								onChange={(event) => field.handleChange(maskBirthDateInput(event.target.value))}
								onBlur={field.handleBlur}
								{...(key ? { errorText: t(key) } : {})}
							/>
						);
					}}
				</form.Field>

				{/* Валидатора у поля нет: значение выбрано с первого рендера
				    («Мужской» по умолчанию), пустым оно не бывает. */}
				<form.Field name="gender">
					{(field) => {
						return (
							<div className="flex flex-col gap-2">
								<span className="t-meta text-fg-muted">{t("about.gender")}</span>
								<RadioSegment
									label={t("about.gender")}
									name="gender"
									value={field.state.value}
									disabled={pending}
									onValueChange={(next) => field.handleChange(next as Gender)}
									options={[
										{ value: "female", label: t("about.genderFemale") },
										{ value: "male", label: t("about.genderMale") },
										// «Не указывать» длиннее соседей на всех локалях — макет даёт ему flex 1.4.
										{ value: "undisclosed", label: t("about.genderUndisclosed"), flex: 1.4 },
									]}
								/>
							</div>
						);
					}}
				</form.Field>
			</StepForm>
		</>
	);
}

/**
 * URL заглушки для выбранного пола. Своё фото параметром не трогается: сервер
 * на него `?gender=` не смотрит, а пересобранная ссылка всё равно ведёт в то же
 * хранилище.
 */
function placeholderUrlFor(avatarUrl: string | null, gender: Gender): string | null {
	if (!avatarUrl) return null;
	const separator = avatarUrl.includes("?") ? "&" : "?";
	return `${avatarUrl}${separator}gender=${gender}`;
}
